import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useDisclosure } from '@mantine/hooks';
import { useTranslation } from 'react-i18next';
import {
  ActionIcon,
  Alert,
  Badge,
  Button,
  Card,
  Divider,
  Group,
  Loader,
  MultiSelect,
  SimpleGrid,
  Stack,
  Switch,
  Text,
  TextInput,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { IconAlertCircle, IconPencil, IconPlus, IconTrash } from '@tabler/icons-react';
import { AppModal } from '../../shared/components/AppModal';
import { ApiError } from '../../shared/api/problem';
import { useWarehouses } from '../warehouses/useWarehouses';
import { CustomerPricingCard } from '../pricing/CustomerPricingCard';
import { CustomerContractsCard } from '../pricing/CustomerContractsCard';
import { ShipToFormModal } from './ShipToFormModal';
import {
  createCustomer,
  deleteShipTo,
  formatShipToAddress,
  listCustomerWarehouses,
  listShipTos,
  setCustomerWarehouses,
  updateCustomer,
  type CustomerDto,
  type CustomerShipToDto,
} from './api';

interface Props {
  opened: boolean;
  onClose: () => void;
  /** Null to create. */
  customer: CustomerDto | null;
  onSaved: () => void;
}

interface FormState {
  customerNumber: string;
  customerName: string;
  companyName: string;
  contactName: string;
  email: string;
  phone: string;
  isActive: boolean;
  warehouseIds: string[];
}

const EMPTY: FormState = {
  customerNumber: '',
  customerName: '',
  companyName: '',
  contactName: '',
  email: '',
  phone: '',
  isActive: true,
  warehouseIds: [],
};

/**
 * Creates or edits one customer, with its warehouses and its delivery addresses.
 *
 * The customer number is set once and read-only afterwards: it identifies the customer to
 * everything outside the database, and the API will not accept a change either.
 *
 * WHY THE ADDRESSES ONLY APPEAR WHEN EDITING
 * -------------------------------------------
 * A ship-to hangs off a customer id, and a customer being created does not have one yet. Rather
 * than collect addresses into local state and replay them after the create — which turns one
 * failed request into a half-saved customer nobody can see — the panel says so and the addresses
 * are added on the next open. Creating a customer is a five-second job; the address is the part
 * with ten fields.
 *
 * There is no delete for the customer itself. Sales orders will reference one, and a shipped order
 * has to keep saying who it went to, so the switch deactivates instead.
 */
export function CustomerFormModal({ opened, onClose, customer, onSaved }: Props) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const isEdit = customer !== null;

  const warehouses = useWarehouses();

  const [form, setForm] = useState<FormState>(EMPTY);
  const [errors, setErrors] = useState<Partial<Record<keyof FormState, string>>>({});
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [shipToModalOpen, { open: openShipTo, close: closeShipTo }] = useDisclosure(false);
  const [editingShipTo, setEditingShipTo] = useState<CustomerShipToDto | null>(null);

  // Fetched rather than carried on the list row: the list knows the customer, not which warehouses
  // it is in.
  const assigned = useQuery({
    queryKey: ['customer-warehouses', customer?.customerId] as const,
    queryFn: ({ signal }) => listCustomerWarehouses(customer!.customerId, signal),
    enabled: opened && isEdit,
  });

  const shipTos = useQuery({
    queryKey: ['customer-ship-tos', customer?.customerId] as const,
    queryFn: ({ signal }) => listShipTos(customer!.customerId, signal),
    enabled: opened && isEdit,
  });

  useEffect(() => {
    if (!opened) return;
    setErrors({});
    setSubmitError(null);
    setEditingShipTo(null);
    setForm({
      customerNumber: customer?.customerNumber ?? '',
      customerName: customer?.customerName ?? '',
      companyName: customer?.companyName ?? '',
      contactName: customer?.contactName ?? '',
      email: customer?.email ?? '',
      phone: customer?.phone ?? '',
      isActive: customer?.isActive ?? true,
      warehouseIds: [],
    });
  }, [opened, customer]);

  // Arrives after the form is seeded, so it is applied separately rather than racing the reset.
  useEffect(() => {
    if (!opened || !assigned.data) return;
    setForm((current) => ({ ...current, warehouseIds: assigned.data.map((w) => w.warehouseId) }));
  }, [opened, assigned.data]);

  const warehouseOptions = useMemo(
    () =>
      (warehouses.data ?? [])
        // An inactive warehouse stays selectable when the customer is already assigned to it:
        // hiding it would silently unassign on the next save.
        .filter((w) => w.isActive || form.warehouseIds.includes(w.warehouseId))
        .map((w) => ({ value: w.warehouseId, label: `${w.warehouseCode} — ${w.displayName}` })),
    [warehouses.data, form.warehouseIds],
  );

  const save = useMutation({
    mutationFn: async () => {
      const body = {
        customerName: form.customerName.trim(),
        companyName: blankToNull(form.companyName),
        contactName: blankToNull(form.contactName),
        email: blankToNull(form.email),
        phone: blankToNull(form.phone),
        isActive: form.isActive,
      };

      const saved = isEdit
        ? await updateCustomer(customer!.customerId, body)
        : await createCustomer({ ...body, customerNumber: form.customerNumber.trim() });

      // Two calls because they are two resources. The customer is saved either way; if the
      // assignment fails the error names that, rather than implying nothing was written.
      await setCustomerWarehouses(saved.customerId, form.warehouseIds);
      return saved;
    },
    onSuccess: () => {
      notifications.show({ message: t('customers.saved'), color: 'green' });
      onSaved();
      onClose();
    },
    onError: (err) => setSubmitError(describe(err)),
  });

  const removeShipTo = useMutation({
    mutationFn: (shipToId: string) => deleteShipTo(customer!.customerId, shipToId),
    onSuccess: () => {
      notifications.show({ message: t('customers.shipTos.deleted'), color: 'green' });
      void queryClient.invalidateQueries({ queryKey: ['customer-ship-tos', customer!.customerId] });
    },
    onError: (err) => setSubmitError(describe(err)),
  });

  function describe(err: unknown): string {
    if (err instanceof ApiError) {
      if (err.problem.type?.includes('duplicate_customer_number')) {
        return t('customers.errors.duplicateNumber', { number: form.customerNumber.trim() });
      }
      return err.problem.detail ?? err.problem.title ?? t('customers.errors.saveFailed');
    }
    return t('customers.errors.saveFailed');
  }

  function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setSubmitError(null);

    const next: typeof errors = {};
    if (!isEdit && !form.customerNumber.trim()) {
      next.customerNumber = t('customers.errors.numberRequired');
    }
    if (!form.customerName.trim()) next.customerName = t('customers.errors.nameRequired');

    setErrors(next);
    if (Object.keys(next).length > 0) return;
    save.mutate();
  }

  const rows = shipTos.data ?? [];

  return (
    <>
      <AppModal
        opened={opened}
        onClose={onClose}
        title={
          isEdit ? t('customers.editTitle', { name: customer!.customerName }) : t('customers.newTitle')
        }
        width="lg"
      >
        <form onSubmit={onSubmit} noValidate>
          <Stack gap="md">
            <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="md">
              <TextInput
                label={t('customers.number')}
                description={isEdit ? t('customers.numberFixed') : t('customers.numberHint')}
                value={form.customerNumber}
                onChange={(e) => setForm({ ...form, customerNumber: e.currentTarget.value })}
                error={errors.customerNumber}
                maxLength={50}
                disabled={isEdit}
                required
                autoFocus={!isEdit}
              />
              <TextInput
                label={t('customers.name')}
                value={form.customerName}
                onChange={(e) => setForm({ ...form, customerName: e.currentTarget.value })}
                error={errors.customerName}
                maxLength={200}
                required
                autoFocus={isEdit}
              />
            </SimpleGrid>

            <TextInput
              label={t('customers.companyName')}
              description={t('customers.companyNameHint')}
              value={form.companyName}
              onChange={(e) => setForm({ ...form, companyName: e.currentTarget.value })}
              maxLength={200}
            />

            <Divider label={t('customers.contactSection')} labelPosition="left" />

            <SimpleGrid cols={{ base: 1, sm: 3 }} spacing="md">
              <TextInput
                label={t('customers.contact')}
                description={t('customers.contactHint')}
                value={form.contactName}
                onChange={(e) => setForm({ ...form, contactName: e.currentTarget.value })}
                maxLength={255}
              />
              <TextInput
                label={t('customers.email')}
                type="email"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.currentTarget.value })}
                maxLength={320}
              />
              <TextInput
                label={t('customers.phone')}
                value={form.phone}
                onChange={(e) => setForm({ ...form, phone: e.currentTarget.value })}
                maxLength={50}
              />
            </SimpleGrid>

            <Divider label={t('customers.warehousesSection')} labelPosition="left" />

            <MultiSelect
              label={t('customers.warehouses')}
              description={t('customers.warehousesHint')}
              data={warehouseOptions}
              value={form.warehouseIds}
              onChange={(v) => setForm({ ...form, warehouseIds: v })}
              disabled={warehouses.isLoading || (isEdit && assigned.isLoading)}
              searchable
              clearable
            />

            <Divider label={t('customers.shipTos.section')} labelPosition="left" />

            {!isEdit && (
              <Alert color="gray" variant="light">
                {t('customers.shipTos.afterCreate')}
              </Alert>
            )}

            {/* Behind pricing:read, so it renders nothing for anyone who only holds
                customers:read -- which is every seeded operator and the read-only group. A
                customer's percentage discount is margin information. */}
            {isEdit && <CustomerPricingCard customerId={customer!.customerId} />}
            {isEdit && <CustomerContractsCard customerId={customer!.customerId} />}

            {isEdit && (
              <Stack gap="sm">
                {shipTos.isLoading && <Loader size="sm" color="invetrak" />}

                {shipTos.isError && (
                  <Alert color="red" variant="light" icon={<IconAlertCircle size={16} />}>
                    {t('customers.shipTos.errors.loadFailed')}
                  </Alert>
                )}

                {shipTos.isSuccess && rows.length === 0 && (
                  <Alert color="yellow" variant="light">
                    {/* Worth a warning rather than a blank: a customer with no address cannot be
                        shipped to, and nothing else on this form says so. */}
                    {t('customers.shipTos.none')}
                  </Alert>
                )}

                {rows.map((s) => (
                  <Card key={s.customerShipToId} withBorder padding="sm" radius="sm">
                    <Group justify="space-between" wrap="nowrap" align="flex-start">
                      <Stack gap={2} style={{ minWidth: 0 }}>
                        <Group gap="xs" wrap="wrap">
                          <Text size="sm" fw={500}>
                            {s.shipToName}
                          </Text>
                          <Badge size="xs" variant="light" color="gray">
                            {s.shipToCode}
                          </Badge>
                          {s.isDefault && (
                            <Badge size="xs" variant="light" color="invetrak">
                              {t('customers.shipTos.defaultBadge')}
                            </Badge>
                          )}
                          {!s.isActive && (
                            <Badge size="xs" variant="light" color="gray">
                              {t('customers.inactiveState')}
                            </Badge>
                          )}
                        </Group>
                        <Text size="xs" c="dimmed">
                          {formatShipToAddress(s)}
                        </Text>
                        {s.phone && (
                          <Text size="xs" c="dimmed">
                            {s.phone}
                          </Text>
                        )}
                      </Stack>
                      <Group gap={4} wrap="nowrap">
                        <ActionIcon
                          variant="subtle"
                          color="invetrak"
                          aria-label={t('customers.shipTos.edit', { name: s.shipToName })}
                          onClick={() => {
                            setEditingShipTo(s);
                            openShipTo();
                          }}
                        >
                          <IconPencil size={16} />
                        </ActionIcon>
                        <ActionIcon
                          variant="subtle"
                          color="red"
                          aria-label={t('customers.shipTos.delete', { name: s.shipToName })}
                          loading={removeShipTo.isPending}
                          onClick={() => removeShipTo.mutate(s.customerShipToId)}
                        >
                          <IconTrash size={16} />
                        </ActionIcon>
                      </Group>
                    </Group>
                  </Card>
                ))}

                <Group>
                  <Button
                    variant="light"
                    color="invetrak"
                    size="xs"
                    leftSection={<IconPlus size={14} />}
                    onClick={() => {
                      setEditingShipTo(null);
                      openShipTo();
                    }}
                  >
                    {t('customers.shipTos.add')}
                  </Button>
                </Group>
              </Stack>
            )}

            <Divider />

            <Switch
              label={t('customers.active')}
              description={t('customers.activeHint')}
              checked={form.isActive}
              onChange={(e) => setForm({ ...form, isActive: e.currentTarget.checked })}
            />

            {submitError && (
              <Alert color="red" variant="light" icon={<IconAlertCircle size={16} />}>
                {submitError}
              </Alert>
            )}

            <Group justify="flex-end">
              <Button variant="default" onClick={onClose}>
                {t('shell.cancel')}
              </Button>
              <Button type="submit" color="invetrak" loading={save.isPending}>
                {t('shell.saveChanges')}
              </Button>
            </Group>
          </Stack>
        </form>
      </AppModal>

      {isEdit && (
        <ShipToFormModal
          opened={shipToModalOpen}
          onClose={closeShipTo}
          customerId={customer!.customerId}
          shipTo={editingShipTo}
          isFirst={rows.length === 0}
          onSaved={() => {
            notifications.show({ message: t('customers.shipTos.saved'), color: 'green' });
            // The whole list, not just the saved row: saving a default demotes another one, and
            // deleting a default promotes a replacement.
            void queryClient.invalidateQueries({
              queryKey: ['customer-ship-tos', customer!.customerId],
            });
          }}
        />
      )}
    </>
  );
}

/** An all-whitespace field becomes null rather than a blank that reads as data. */
function blankToNull(value: string): string | null {
  const trimmed = value.trim();
  return trimmed.length === 0 ? null : trimmed;
}
